import React, { useEffect, useState } from "react";
import { api, formatApiError } from "../../lib/api";
import { useI18n } from "../../contexts/I18nContext";
import { Plus, Trash2, Edit, X } from "lucide-react";

const empty = { code: "", discount_pct: "", discount_amount_eur: "", valid_from: "", valid_to: "", max_uses: "", active: true, is_public: false, title_ro: "", title_en: "", description_ro: "", description_en: "" };

export default function AdminCoupons() {
  const { t } = useI18n();
  const [coupons, setCoupons] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [error, setError] = useState("");

  const load = async () => setCoupons((await api.get("/admin/coupons")).data);
  useEffect(() => { load(); }, []);

  const open = (c) => {
    setEditing(c?.id || null);
    setForm(c ? {
      ...c,
      discount_pct: c.discount_pct ?? "",
      discount_amount_eur: c.discount_amount_eur ?? "",
      valid_from: c.valid_from ?? "",
      valid_to: c.valid_to ?? "",
      max_uses: c.max_uses ?? "",
    } : empty);
    setError("");
  };

  const save = async () => {
    setError("");
    try {
      const payload = {
        code: form.code,
        discount_pct: form.discount_pct === "" ? null : Number(form.discount_pct),
        discount_amount_eur: form.discount_amount_eur === "" ? null : Number(form.discount_amount_eur),
        valid_from: form.valid_from || null,
        valid_to: form.valid_to || null,
        max_uses: form.max_uses === "" ? null : Number(form.max_uses),
        active: !!form.active,
        is_public: !!form.is_public,
        title_ro: form.title_ro || "",
        title_en: form.title_en || "",
        description_ro: form.description_ro || "",
        description_en: form.description_en || "",
      };
      if (editing) await api.put(`/admin/coupons/${editing}`, payload);
      else await api.post("/admin/coupons", payload);
      await load();
      setEditing(null);
      setForm(empty);
    } catch (e) { setError(formatApiError(e)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Stergi cuponul?")) return;
    await api.delete(`/admin/coupons/${id}`);
    await load();
  };

  return (
    <div data-testid="admin-coupons">
      <div className="flex items-center justify-between mb-10">
        <div>
          <div className="label-eyebrow mb-2" style={{ color: "var(--brand-primary)" }}>{t("admin.dashboard")}</div>
          <h1 className="font-serif text-3xl sm:text-4xl">Coduri reducere</h1>
        </div>
        <button onClick={() => open(null)} className="btn-gold-filled flex items-center gap-2" data-testid="add-coupon-btn">
          <Plus className="w-4 h-4" /> Adauga cupon
        </button>
      </div>

      <table className="w-full text-sm card-surface mb-6">
        <thead>
          <tr className="border-b" style={{ borderColor: "var(--brand-border)" }}>
            <th className="text-left p-4 label-eyebrow">Cod</th>
            <th className="text-left p-4 label-eyebrow">Reducere</th>
            <th className="text-left p-4 label-eyebrow">Valabilitate</th>
            <th className="text-left p-4 label-eyebrow">Folosit</th>
            <th className="text-left p-4 label-eyebrow">Activ</th>
            <th className="text-left p-4 label-eyebrow">Public</th>
            <th className="text-right p-4 label-eyebrow">Actiuni</th>
          </tr>
        </thead>
        <tbody>
          {coupons.map((c) => (
            <tr key={c.id} className="border-b" style={{ borderColor: "var(--brand-border)" }} data-testid={`coupon-${c.id}`}>
              <td className="p-4 font-mono" style={{ color: "var(--brand-primary)" }}>{c.code}</td>
              <td className="p-4">{c.discount_pct ? `${c.discount_pct}%` : c.discount_amount_eur ? `€${c.discount_amount_eur}` : "-"}</td>
              <td className="p-4" style={{ color: "var(--brand-text-secondary)" }}>
                {c.valid_from || "..."} → {c.valid_to || "..."}
              </td>
              <td className="p-4">{c.used_count}{c.max_uses ? ` / ${c.max_uses}` : ""}</td>
              <td className="p-4">{c.active ? "Da" : "Nu"}</td>
              <td className="p-4">{c.is_public ? <span style={{ color: "var(--brand-primary)" }}>★ Da</span> : "Nu"}</td>
              <td className="p-4 text-right">
                <button onClick={() => open(c)} className="btn-ghost mr-2" data-testid={`edit-coupon-${c.id}`}><Edit className="w-3.5 h-3.5" /></button>
                <button onClick={() => remove(c.id)} className="btn-ghost" data-testid={`delete-coupon-${c.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
              </td>
            </tr>
          ))}
          {coupons.length === 0 && (
            <tr><td colSpan={7} className="text-center py-10 text-sm" style={{ color: "var(--brand-text-secondary)" }}>Niciun cupon</td></tr>
          )}
        </tbody>
      </table>

      {(editing !== null || form !== empty) && (form.code !== "" || editing) && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-6" onClick={() => { setEditing(null); setForm(empty); }}>
          <div className="card-surface p-5 sm:p-8 w-full max-w-lg max-h-[90vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-serif text-2xl">{editing ? "Editeaza cupon" : "Cupon nou"}</h2>
              <button onClick={() => { setEditing(null); setForm(empty); }} data-testid="close-coupon-form"><X className="w-5 h-5" /></button>
            </div>
            <div className="grid grid-cols-2 gap-3 mb-4">
              <Field label="Cod"><input className="brand-input" value={form.code} onChange={(e) => setForm({...form, code: e.target.value.toUpperCase()})} data-testid="coupon-code" /></Field>
              <Field label="Reducere %"><input type="number" className="brand-input" value={form.discount_pct} onChange={(e) => setForm({...form, discount_pct: e.target.value})} data-testid="coupon-pct" /></Field>
              <Field label="Reducere EUR"><input type="number" className="brand-input" value={form.discount_amount_eur} onChange={(e) => setForm({...form, discount_amount_eur: e.target.value})} data-testid="coupon-eur" /></Field>
              <Field label="Max folosiri"><input type="number" className="brand-input" value={form.max_uses} onChange={(e) => setForm({...form, max_uses: e.target.value})} data-testid="coupon-max" /></Field>
              <Field label="Valabil de la"><input type="date" className="brand-input" value={form.valid_from} onChange={(e) => setForm({...form, valid_from: e.target.value})} data-testid="coupon-from" /></Field>
              <Field label="Valabil pana la"><input type="date" className="brand-input" value={form.valid_to} onChange={(e) => setForm({...form, valid_to: e.target.value})} data-testid="coupon-to" /></Field>
            </div>

            <div className="border-t pt-4 mt-2 mb-4" style={{ borderColor: "var(--brand-border)" }}>
              <label className="flex items-center gap-2 mb-3 text-sm cursor-pointer">
                <input type="checkbox" checked={!!form.is_public} onChange={(e) => setForm({...form, is_public: e.target.checked})} data-testid="coupon-is-public" />
                <span><b>Afiseaza pe pagina publica /oferte</b></span>
              </label>
              {form.is_public && (
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Titlu RO"><input className="brand-input" value={form.title_ro || ""} placeholder="Oferta de Pasti" onChange={(e) => setForm({...form, title_ro: e.target.value})} data-testid="coupon-title-ro" /></Field>
                  <Field label="Titlu EN"><input className="brand-input" value={form.title_en || ""} placeholder="Easter Offer" onChange={(e) => setForm({...form, title_en: e.target.value})} data-testid="coupon-title-en" /></Field>
                  <Field label="Descriere RO"><input className="brand-input" value={form.description_ro || ""} placeholder="Min. 3 zile..." onChange={(e) => setForm({...form, description_ro: e.target.value})} data-testid="coupon-desc-ro" /></Field>
                  <Field label="Descriere EN"><input className="brand-input" value={form.description_en || ""} placeholder="Min. 3 days..." onChange={(e) => setForm({...form, description_en: e.target.value})} data-testid="coupon-desc-en" /></Field>
                </div>
              )}
            </div>

            <label className="flex items-center gap-2 mb-4 text-sm">
              <input type="checkbox" checked={form.active} onChange={(e) => setForm({...form, active: e.target.checked})} data-testid="coupon-active" />
              Activ
            </label>
            {error && <div className="text-sm mb-3" style={{ color: "var(--brand-primary)" }}>{error}</div>}
            <div className="flex gap-3">
              <button onClick={save} className="btn-gold-filled flex-1" data-testid="save-coupon-btn">Salveaza</button>
              <button onClick={() => { setEditing(null); setForm(empty); }} className="btn-ghost">Anuleaza</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="label-eyebrow block mb-2">{label}</label>
      {children}
    </div>
  );
}
